/** Whether a derived account situation is currently open. */
export enum AccountSituationStatus {
  Open = 'OPEN',
  Resolved = 'RESOLVED',
}

/** An account-level situation derived from email thread metadata. */
export enum AccountSituationType {
  AwaitingThem = 'AWAITING_THEM',
  AwaitingUs = 'AWAITING_US',
  ChampionLost = 'CHAMPION_LOST',
  CommitmentOverdue = 'COMMITMENT_OVERDUE',
  GoingDark = 'GOING_DARK',
  Multithreading = 'MULTITHREADING',
  SingleThreaded = 'SINGLE_THREADED',
  Stalled = 'STALLED',
}

/** WORKSPACE applies to every member (admins save and remove); PERSONAL to its owner only */
export enum AgentMemoryScope {
  Personal = 'PERSONAL',
  Workspace = 'WORKSPACE',
}

/** Resolutions an agent may close a ticket with. */
export enum AgentTicketResolution {
  AgentResolved = 'AGENT_RESOLVED',
  Duplicate = 'DUPLICATE',
  WontFix = 'WONT_FIX',
}

/** INCLUDED and ABSORBED use no credits. PER_ACTION uses a fixed number per call. COST_MULTIPLE uses a number of credits per dollar of provider cost. */
export enum AiCreditRuleKind {
  Absorbed = 'ABSORBED',
  CostMultiple = 'COST_MULTIPLE',
  Included = 'INCLUDED',
  PerAction = 'PER_ACTION',
}

/** What a scheduled charge is worth. BASE and USAGE point at the existing pricing engine; FLAT is a fixed amount; PERCENT_OF_BASE is a share of the covered base price. */
export enum AmountRuleType {
  Base = 'BASE',
  Flat = 'FLAT',
  PercentOfBase = 'PERCENT_OF_BASE',
  Usage = 'USAGE',
}

/** Event a relative anchor resolves against. */
export enum AnchorEventType {
  ContractExpiry = 'CONTRACT_EXPIRY',
  CustomDate = 'CUSTOM_DATE',
  EmailOpened = 'EMAIL_OPENED',
  LeadCreated = 'LEAD_CREATED',
  LeadStageChanged = 'LEAD_STAGE_CHANGED',
  SubscriptionEnd = 'SUBSCRIPTION_END',
  SubscriptionRenewal = 'SUBSCRIPTION_RENEWAL',
}

/** Absolute vs relative scheduling. */
export enum AnchorType {
  Absolute = 'ABSOLUTE',
  Relative = 'RELATIVE',
}

/** Lifecycle state of an API key (PRD §12.8) */
export enum ApiKeyStatus {
  Active = 'ACTIVE',
  Revoked = 'REVOKED',
  Rotating = 'ROTATING',
}

/** Assignee kind. v1 accepts USER only; AGENT is reserved. */
export enum AssigneeType {
  Agent = 'AGENT',
  User = 'USER',
}

export enum AssistantOnboardingMode {
  Docked = 'DOCKED',
  FullPage = 'FULL_PAGE',
}

export enum AssistantOnboardingPhase {
  Complete = 'COMPLETE',
  Discovery = 'DISCOVERY',
  Tour = 'TOUR',
  TrialSetup = 'TRIAL_SETUP',
}

/** Which identifier scheme a bank account entry uses, so only the relevant fields need to be filled in. */
export enum BankAccountType {
  Iban = 'IBAN',
  RoutingSort = 'ROUTING_SORT',
  Swift = 'SWIFT',
}

/** Which number in the period is rated: MAX (highest observed), TOTAL (sum over the period), or CLOSING (value at period end). */
export enum BilledValue {
  Closing = 'CLOSING',
  Max = 'MAX',
  Total = 'TOTAL',
}

/** What a prepaid span means on cancellation. Governs cancellation only, never invoicing: BINDING owes the span and extends the term to it, REFUNDABLE credits the unconsumed whole periods back, NONE is standard cancellation. */
export enum BillingCommitment {
  Binding = 'BINDING',
  None = 'NONE',
  Refundable = 'REFUNDABLE',
}

/** Lifecycle of a billing schedule: PENDING (awaiting activation) → ACTIVE → COMPLETE, or CANCELLED when the subscription is cancelled first. */
export enum BillingScheduleStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Complete = 'COMPLETE',
  Pending = 'PENDING',
}

/** Whether the component is billed at period start (ADVANCE) or end (ARREARS) */
export enum BillingTiming {
  Advance = 'ADVANCE',
  Arrears = 'ARREARS',
}

/** Resolution mode for bulk discrepancy resolution */
export enum BulkResolutionMode {
  AcceptSource_1 = 'ACCEPT_SOURCE_1',
  AcceptSource_2 = 'ACCEPT_SOURCE_2',
}

/** What started a website pull */
export enum BusinessProfilePullTrigger {
  Manual = 'MANUAL',
  OkrCycleCreated = 'OKR_CYCLE_CREATED',
}

export enum CalendlyMappingSource {
  EmailMatch = 'EMAIL_MATCH',
  Manual = 'MANUAL',
}

/** Parent entity kind a cascaded draft was created on. */
export enum CascadeEntityType {
  Bundle = 'BUNDLE',
  Module = 'MODULE',
}

/** Kind of catalog item in the unified Products surface (MODULE | BUNDLE | COMPONENT). */
export enum CatalogItemType {
  Bundle = 'BUNDLE',
  Component = 'COMPONENT',
  Module = 'MODULE',
}

/** Whether a scheduled charge fires once or on every qualifying period. */
export enum ChargeRecurrence {
  Once = 'ONCE',
  Recurring = 'RECURRING',
}

/** When a scheduled charge is invoiced. SIGNING and ACTIVATION are fired by the billing-schedule service; PERIOD_START and PERIOD_END describe the existing renewal pipeline; MILESTONE and EXPLICIT_DATE are accepted by the schema but have no firing path yet. */
export enum ChargeTrigger {
  Activation = 'ACTIVATION',
  ExplicitDate = 'EXPLICIT_DATE',
  Milestone = 'MILESTONE',
  PeriodEnd = 'PERIOD_END',
  PeriodStart = 'PERIOD_START',
  Signing = 'SIGNING',
}

/** Author of a chat turn in the creation flow. */
export enum ChatTurnRole {
  Assistant = 'ASSISTANT',
  User = 'USER',
}

export enum CheckInChannel {
  Clickup = 'CLICKUP',
  Slack = 'SLACK',
  Web = 'WEB',
}

export enum CheckinAskTimeMode {
  Fixed = 'FIXED',
  ParticipantLocal = 'PARTICIPANT_LOCAL',
}

export enum CheckinDaysMode {
  Custom = 'CUSTOM',
  Tenant = 'TENANT',
}

export enum CheckinParticipantReachability {
  Deactivated = 'DEACTIVATED',
  Reachable = 'REACHABLE',
  Unmapped = 'UNMAPPED',
}

export enum CheckinPauseReason {
  Admin = 'ADMIN',
  SlackDisconnected = 'SLACK_DISCONNECTED',
}

export enum CheckinQuestionColor {
  Blue = 'BLUE',
  Brown = 'BROWN',
  Green = 'GREEN',
  Orange = 'ORANGE',
  Purple = 'PURPLE',
  Red = 'RED',
  Yellow = 'YELLOW',
}

export enum CheckinQuestionType {
  LongText = 'LONG_TEXT',
  Scale_1_5 = 'SCALE_1_5',
  ShortText = 'SHORT_TEXT',
  SingleChoice = 'SINGLE_CHOICE',
  YesNo = 'YES_NO',
}

export enum CheckinStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
  Paused = 'PAUSED',
}

/** Reason a subscription churned. Finance splits involuntary (recoverable, e.g. PAYMENT_FAILURE) from voluntary (product signal, e.g. CUSTOMER_REQUEST). */
export enum ChurnReason {
  CustomerRequest = 'CUSTOMER_REQUEST',
  Involuntary = 'INVOLUNTARY',
  Migration = 'MIGRATION',
  NaturalExpiration = 'NATURAL_EXPIRATION',
  NonRenewal = 'NON_RENEWAL',
  Other = 'OTHER',
  PaymentFailure = 'PAYMENT_FAILURE',
  UpgradeDowngrade = 'UPGRADE_DOWNGRADE',
  Voluntary = 'VOLUNTARY',
}

export enum ClickUpConnectionStatus {
  Connected = 'CONNECTED',
  Disconnected = 'DISCONNECTED',
  Revoked = 'REVOKED',
}

/** A ClickUp Space, Folder or List (every Doc in it), or one Doc. */
export enum ClickUpDocScopeKind {
  Doc = 'DOC',
  Folder = 'FOLDER',
  List = 'LIST',
  Space = 'SPACE',
}

export enum ClickUpMappingSource {
  EmailMatch = 'EMAIL_MATCH',
  Manual = 'MANUAL',
}

/** Whether one client’s rows in a sync job can be committed yet */
export enum ClientCommitState {
  Committed = 'COMMITTED',
  NothingToCommit = 'NOTHING_TO_COMMIT',
  Ready = 'READY',
  Waiting = 'WAITING',
}

/** When the subscription period clock starts: at sale (today’s behaviour) or at activation (the subscription waits as PENDING until the go-live action fires). */
export enum ClockStart {
  Activation = 'ACTIVATION',
  Sale = 'SALE',
}

/** Whether commission applies to gross sale value or net of fees */
export enum CommissionApplyTo {
  Gross = 'GROSS',
  Net = 'NET',
}

/** Which side of the conversation owes a commitment. */
export enum CommitmentOwner {
  Them = 'THEM',
  Us = 'US',
}

/** Where a commitment stands: upcoming, overdue, or past due with activity since. */
export enum CommitmentStatus {
  ActivitySince = 'ACTIVITY_SINCE',
  Overdue = 'OVERDUE',
  Upcoming = 'UPCOMING',
}

/** How a preview field was read: the site's structured data (JSON-LD), a meta tag, the page title, a fixed pattern in the page text (VAT and registration numbers, legal names), a link on the page, or the domain itself (country from .be, .fr…). */
export enum CompanyFactMethod {
  Domain = 'DOMAIN',
  Link = 'LINK',
  MetaTag = 'META_TAG',
  PageText = 'PAGE_TEXT',
  PageTitle = 'PAGE_TITLE',
  StructuredData = 'STRUCTURED_DATA',
}

/** Why no preview could be made. INVALID_INPUT: nothing domain-shaped was typed. FREEMAIL: the domain is a consumer mailbox provider (gmail.com), which identifies no company. */
export enum CompanyPreviewProblem {
  Freemail = 'FREEMAIL',
  InvalidInput = 'INVALID_INPUT',
}

/** How completion is checked for a step */
export enum CompletionCheckType {
  Auto = 'AUTO',
  Manual = 'MANUAL',
}

/** Type of auto-completion criteria */
export enum CompletionCriteriaType {
  Count = 'COUNT',
  FieldSet = 'FIELD_SET',
  HasData = 'HAS_DATA',
}

/** Where a same-domain contact suggestion was found. */
export enum ContactSuggestionSource {
  EmailParticipant = 'EMAIL_PARTICIPANT',
  UnassignedContact = 'UNASSIGNED_CONTACT',
}

/** An operational clause tracked across contracts. */
export enum ContractClauseKind {
  BreachNotification = 'BREACH_NOTIFICATION',
  SubprocessorChange = 'SUBPROCESSOR_CHANGE',
}

export enum ContractClauseSource {
  Document = 'DOCUMENT',
  Manual = 'MANUAL',
}

/** Where a client stands on one tracked clause. */
export enum ContractClauseStanding {
  NotRecorded = 'NOT_RECORDED',
  NotStated = 'NOT_STATED',
  Stated = 'STATED',
}

export enum ContractDateUnit {
  Day = 'DAY',
  Month = 'MONTH',
  Week = 'WEEK',
  Year = 'YEAR',
}

export enum ContractFieldFilledBy {
  Computed = 'COMPUTED',
  Sender = 'SENDER',
  Signer = 'SIGNER',
}

export enum ContractFieldType {
  Choice = 'CHOICE',
  Date = 'DATE',
  Number = 'NUMBER',
  Text = 'TEXT',
}

export enum ContractInviteStatus {
  Failed = 'FAILED',
  Manual = 'MANUAL',
  Pending = 'PENDING',
  Sent = 'SENT',
  Skipped = 'SKIPPED',
  Waiting = 'WAITING',
}

export enum ContractOrderFormFilledBy {
  Sender = 'SENDER',
  Template = 'TEMPLATE',
}

export enum ContractRecipientRole {
  Cc = 'CC',
  Signer = 'SIGNER',
}

/** What happens at the end of the committed term. Governs the commitment boundary only — ordinary period boundaries inside the term stay with the subscription's autoRenew flag. */
export enum ContractRenewalTerms {
  AutoRenew = 'AUTO_RENEW',
  None = 'NONE',
  NoticeDays = 'NOTICE_DAYS',
}

export enum ContractRequestStatus {
  Cancelled = 'CANCELLED',
  Opened = 'OPENED',
  Sent = 'SENT',
  Signed = 'SIGNED',
}

export enum ContractSendPlanStatus {
  Cancelled = 'CANCELLED',
  NeedsAttention = 'NEEDS_ATTENTION',
  Sent = 'SENT',
  Waiting = 'WAITING',
}

export enum ContractSlotSide {
  Client = 'CLIENT',
  Company = 'COMPANY',
}

/** Where a contract sits in its signing lifecycle. */
export enum ContractStatus {
  Cancelled = 'CANCELLED',
  Draft = 'DRAFT',
  Expired = 'EXPIRED',
  PendingSignature = 'PENDING_SIGNATURE',
  Signed = 'SIGNED',
}

export enum ContractTemplateStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
  /** Never published: it exists only as a draft and cannot be sent. */
  Draft = 'DRAFT',
}

/** Kind of agreement. An MSA is the framework and is usually evergreen; a SOW is where a committed term normally lives. */
export enum ContractType {
  Amendment = 'AMENDMENT',
  Msa = 'MSA',
  Nda = 'NDA',
  Other = 'OTHER',
  Sow = 'SOW',
}

/** How a conversation thread is shown: to the whole team, only to its owner, or hidden from the default list. */
export enum ConversationThreadVisibilityState {
  Hidden = 'HIDDEN',
  Private = 'PRIVATE',
  Shared = 'SHARED',
}

/** How a CRM record was created (provenance). */
export enum CrmCreatedVia {
  Agent = 'AGENT',
  Api = 'API',
  Direct = 'DIRECT',
  Integration = 'INTEGRATION',
}

/** CRM document category. */
export enum CrmDocumentCategory {
  Contract = 'CONTRACT',
  CreditNote = 'CREDIT_NOTE',
  Deck = 'DECK',
  Invoice = 'INVOICE',
  Other = 'OTHER',
  ProofOfPayment = 'PROOF_OF_PAYMENT',
  Proposal = 'PROPOSAL',
}

/** Who set a document mark. */
export enum CrmDocumentMarkSource {
  Folder = 'FOLDER',
  SuggestionConfirmed = 'SUGGESTION_CONFIRMED',
  System = 'SYSTEM',
  User = 'USER',
}

/** Where an Invoice or Credit note mark is: marked, in review, imported, paid. */
export enum CrmDocumentMarkState {
  Imported = 'IMPORTED',
  InReview = 'IN_REVIEW',
  Marked = 'MARKED',
  Paid = 'PAID',
  PartlyPaid = 'PARTLY_PAID',
}

/** Whether a CRM document was uploaded by a person or filed from an email. */
export enum CrmDocumentSource {
  Drive = 'DRIVE',
  Email = 'EMAIL',
  Upload = 'UPLOAD',
}

/** A mark-based view of a record’s documents: unmarked, to import, paid or unpaid invoices. */
export enum CrmDocumentView {
  PaidInvoices = 'PAID_INVOICES',
  ToImport = 'TO_IMPORT',
  Unmarked = 'UNMARKED',
  UnpaidInvoices = 'UNPAID_INVOICES',
}

/** Marketing-email consent state for a contact. */
export enum CrmEmailConsent {
  Bounced = 'BOUNCED',
  Subscribed = 'SUBSCRIBED',
  Unsubscribed = 'UNSUBSCRIBED',
}

/** Email direction. */
export enum CrmEmailDirection {
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND',
}

/** Email log origin. */
export enum CrmEmailOrigin {
  Manual = 'MANUAL',
  Synced = 'SYNCED',
}

/** Outcome of a CRM contact and company import batch. */
export enum CrmImportBatchStatus {
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Partial = 'PARTIAL',
  RolledBack = 'ROLLED_BACK',
}

/** Which entity a CRM import failure refers to. */
export enum CrmImportEntityType {
  Company = 'COMPANY',
  Contact = 'CONTACT',
  Row = 'ROW',
}

/** Whether the deal is aimed at a module or a bundle. */
export enum CrmInterestedPlanKind {
  Bundle = 'BUNDLE',
  Module = 'MODULE',
}

/** Relationship funnel stage for an organization or contact. */
export enum CrmLifecycleStage {
  Client = 'CLIENT',
  Disqualified = 'DISQUALIFIED',
  Lead = 'LEAD',
  Prospect = 'PROSPECT',
}

/** Whether an attendee is a platform user, a known contact, or an outside address. */
export enum CrmMeetingAttendeeKind {
  Contact = 'CONTACT',
  External = 'EXTERNAL',
  User = 'USER',
}

/** Attendee RSVP state; only a syncing source populates it. */
export enum CrmMeetingAttendeeResponse {
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
  NeedsAction = 'NEEDS_ACTION',
  Tentative = 'TENTATIVE',
}

/** How a meeting is held. */
export enum CrmMeetingLocationType {
  InPerson = 'IN_PERSON',
  Other = 'OTHER',
  Phone = 'PHONE',
  Video = 'VIDEO',
}

/** How the meeting came to be booked. */
export enum CrmMeetingScheduledVia {
  Calendly = 'CALENDLY',
  Email = 'EMAIL',
  Inbound = 'INBOUND',
  Manual = 'MANUAL',
  Other = 'OTHER',
  RevvlyScheduler = 'REVVLY_SCHEDULER',
}

/** System a meeting record or one of its facts came from. */
export enum CrmMeetingSource {
  Calendly = 'CALENDLY',
  Google = 'GOOGLE',
  Ics = 'ICS',
  Manual = 'MANUAL',
  Microsoft = 'MICROSOFT',
  RevvlyScheduler = 'REVVLY_SCHEDULER',
}

/** Whether a meeting is upcoming, happened, was a no-show, or was cancelled. */
export enum CrmMeetingStatus {
  Cancelled = 'CANCELLED',
  Held = 'HELD',
  NoShow = 'NO_SHOW',
  Scheduled = 'SCHEDULED',
}

/** Kind of item in a CRM subject timeline. */
export enum CrmTimelineItemType {
  Document = 'DOCUMENT',
  Email = 'EMAIL',
  FormSubmission = 'FORM_SUBMISSION',
  Meeting = 'MEETING',
  Note = 'NOTE',
  SyncedEmail = 'SYNCED_EMAIL',
  Task = 'TASK',
}

/** Customer-success lifecycle of a client. Separate from the sales funnel stage. */
export enum CsLifecycleStatus {
  Churned = 'CHURNED',
  Nurturing = 'NURTURING',
  Onboarding = 'ONBOARDING',
  Renewal = 'RENEWAL',
  Trial = 'TRIAL',
}

/** Supported currencies for regions */
export enum Currency {
  Aed = 'AED',
  Afn = 'AFN',
  All = 'ALL',
  Amd = 'AMD',
  Ang = 'ANG',
  Aoa = 'AOA',
  Ars = 'ARS',
  Aud = 'AUD',
  Awg = 'AWG',
  Azn = 'AZN',
  Bam = 'BAM',
  Bbd = 'BBD',
  Bdt = 'BDT',
  Bgn = 'BGN',
  Bhd = 'BHD',
  Bmd = 'BMD',
  Bnd = 'BND',
  Bob = 'BOB',
  Brl = 'BRL',
  Bsd = 'BSD',
  Btn = 'BTN',
  Bwp = 'BWP',
  Byn = 'BYN',
  Bzd = 'BZD',
  Cad = 'CAD',
  Cdf = 'CDF',
  Chf = 'CHF',
  Clp = 'CLP',
  Cny = 'CNY',
  Cop = 'COP',
  Crc = 'CRC',
  Cup = 'CUP',
  Cve = 'CVE',
  Czk = 'CZK',
  Djf = 'DJF',
  Dkk = 'DKK',
  Dop = 'DOP',
  Dzd = 'DZD',
  Egp = 'EGP',
  Ern = 'ERN',
  Etb = 'ETB',
  Eur = 'EUR',
  Fjd = 'FJD',
  Fkp = 'FKP',
  Gbp = 'GBP',
  Gel = 'GEL',
  Ghs = 'GHS',
  Gip = 'GIP',
  Gmd = 'GMD',
  Gtq = 'GTQ',
  Gyd = 'GYD',
  Hkd = 'HKD',
  Hnl = 'HNL',
  Htg = 'HTG',
  Huf = 'HUF',
  Idr = 'IDR',
  Ils = 'ILS',
  Inr = 'INR',
  Iqd = 'IQD',
  Irr = 'IRR',
  Isk = 'ISK',
  Jmd = 'JMD',
  Jod = 'JOD',
  Jpy = 'JPY',
  Kes = 'KES',
  Kgs = 'KGS',
  Khr = 'KHR',
  Krw = 'KRW',
  Kwd = 'KWD',
  Kyd = 'KYD',
  Kzt = 'KZT',
  Lak = 'LAK',
  Lbp = 'LBP',
  Lkr = 'LKR',
  Lrd = 'LRD',
  Lyd = 'LYD',
  Mad = 'MAD',
  Mdl = 'MDL',
  Mga = 'MGA',
  Mkd = 'MKD',
  Mmk = 'MMK',
  Mnt = 'MNT',
  Mop = 'MOP',
  Mru = 'MRU',
  Mur = 'MUR',
  Mvr = 'MVR',
  Mwk = 'MWK',
  Mxn = 'MXN',
  Myr = 'MYR',
  Mzn = 'MZN',
  Nad = 'NAD',
  Ngn = 'NGN',
  Nio = 'NIO',
  Nok = 'NOK',
  Npr = 'NPR',
  Nzd = 'NZD',
  Omr = 'OMR',
  Pab = 'PAB',
  Pen = 'PEN',
  Pgk = 'PGK',
  Php = 'PHP',
  Pkr = 'PKR',
  Pln = 'PLN',
  Pyg = 'PYG',
  Qar = 'QAR',
  Ron = 'RON',
  Rsd = 'RSD',
  Rub = 'RUB',
  Rwf = 'RWF',
  Sar = 'SAR',
  Sbd = 'SBD',
  Scr = 'SCR',
  Sdg = 'SDG',
  Sek = 'SEK',
  Sgd = 'SGD',
  Sll = 'SLL',
  Sos = 'SOS',
  Srd = 'SRD',
  Ssp = 'SSP',
  Stn = 'STN',
  Svc = 'SVC',
  Szl = 'SZL',
  Thb = 'THB',
  Tjs = 'TJS',
  Tmt = 'TMT',
  Tnd = 'TND',
  Top = 'TOP',
  Try = 'TRY',
  Ttd = 'TTD',
  Twd = 'TWD',
  Tzs = 'TZS',
  Uah = 'UAH',
  Ugx = 'UGX',
  Usd = 'USD',
  Uyu = 'UYU',
  Uzs = 'UZS',
  Ves = 'VES',
  Vnd = 'VND',
  Vuv = 'VUV',
  Wst = 'WST',
  Xaf = 'XAF',
  Xcd = 'XCD',
  Xof = 'XOF',
  Xpf = 'XPF',
  Yer = 'YER',
  Zar = 'ZAR',
  Zmw = 'ZMW',
  Zwl = 'ZWL',
}

/** Axes a custom pricing agreement is permitted to override */
export enum CustomAgreementOverrideAxis {
  BillingTiming = 'BILLING_TIMING',
  Currency = 'CURRENCY',
  Model = 'MODEL',
  Price = 'PRICE',
  Proration = 'PRORATION',
}

/** Lifecycle state of a custom pricing agreement */
export enum CustomAgreementStatus {
  Draft = 'DRAFT',
  Expired = 'EXPIRED',
  Published = 'PUBLISHED',
  Revoked = 'REVOKED',
}

/** The record type a custom field or tag applies to. */
export enum CustomFieldEntityType {
  Contact = 'CONTACT',
  Lead = 'LEAD',
  Organization = 'ORGANIZATION',
}

export enum CustomFieldType {
  Boolean = 'BOOLEAN',
  Currency = 'CURRENCY',
  Date = 'DATE',
  Location = 'LOCATION',
  Multiselect = 'MULTISELECT',
  Number = 'NUMBER',
  Select = 'SELECT',
  Text = 'TEXT',
  Url = 'URL',
  User = 'USER',
}

export enum CycleState {
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  Draft = 'DRAFT',
  Scoring = 'SCORING',
}

/** Task description storage format. Only MARKDOWN is active in v1. */
export enum DescriptionFormat {
  Markdown = 'MARKDOWN',
  Plain = 'PLAIN',
}

/** Library definition lifecycle: active, stopped (no new use), archived (instances terminated). */
export enum DiscountDefinitionStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
  Stopped = 'STOPPED',
}

/** Discount duration: the first order (first invoice) only, a fixed number of billing cycles, or forever. */
export enum DiscountDurationType {
  FirstOnly = 'FIRST_ONLY',
  FixedCount = 'FIXED_COUNT',
  Forever = 'FOREVER',
}

/** Discount audit action (instance applied/overwritten/cancelled/expired; definition created/edited/stopped/archived). */
export enum DiscountHistoryAction {
  Applied = 'APPLIED',
  Archived = 'ARCHIVED',
  Cancelled = 'CANCELLED',
  Created = 'CREATED',
  Edited = 'EDITED',
  Expired = 'EXPIRED',
  Overwritten = 'OVERWRITTEN',
  Stopped = 'STOPPED',
}

/** Whether an audit entry concerns a discount instance or a library definition. */
export enum DiscountHistoryEntityType {
  Definition = 'DEFINITION',
  Instance = 'INSTANCE',
}

/** Discount instance lifecycle: active, expired (duration reached), or cancelled. */
export enum DiscountInstanceStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
}

/** How multiple discount instances on the same target compose: best wins, first wins, latest wins, or combine (default). */
export enum DiscountStackingMode {
  BestWins = 'BEST_WINS',
  Combine = 'COMBINE',
  FirstWins = 'FIRST_WINS',
  LatestWins = 'LATEST_WINS',
}

/** What a discount instance reduces: the base price, one variable component, or the whole invoice. */
export enum DiscountTargetType {
  BasePrice = 'BASE_PRICE',
  Component = 'COMPONENT',
  Invoice = 'INVOICE',
}

/** Percentage (currency-agnostic) or fixed amount (per-currency values). */
export enum DiscountType {
  Fixed = 'FIXED',
  Percentage = 'PERCENTAGE',
}

export enum DiscoveryQuestion {
  ClientFacingApp = 'CLIENT_FACING_APP',
  CurrentTools = 'CURRENT_TOOLS',
  Description = 'DESCRIPTION',
  Domain = 'DOMAIN',
  Priorities = 'PRIORITIES',
  RevenueModel = 'REVENUE_MODEL',
  SalesMotion = 'SALES_MOTION',
  StrategyFramework = 'STRATEGY_FRAMEWORK',
}

/** Resolution status of a discrepancy */
export enum DiscrepancyResolution {
  Pending = 'PENDING',
  Resolved = 'RESOLVED',
}

/** Source type for discrepancy comparison */
export enum DiscrepancySourceType {
  Excel = 'EXCEL',
  GoogleDrive = 'GOOGLE_DRIVE',
  GoogleDriveResync = 'GOOGLE_DRIVE_RESYNC',
  System = 'SYSTEM',
}

/** What a share link was sent in. */
export enum DocumentShareSourceType {
  Email = 'EMAIL',
  SupportTicket = 'SUPPORT_TICKET',
}

/** Type of subscription document */
export enum DocumentType {
  Amendment = 'AMENDMENT',
  Contract = 'CONTRACT',
  Invoice = 'INVOICE',
  Msa = 'MSA',
  Other = 'OTHER',
}

/** A browser viewed the shared document, or downloaded it. */
export enum DocumentViewKind {
  Download = 'DOWNLOAD',
  View = 'VIEW',
  Visit = 'VISIT',
}

export enum DriveFolderLayout {
  BySource = 'BY_SOURCE',
  Flat = 'FLAT',
}

export enum DriveFolderLinkStatus {
  Active = 'ACTIVE',
  Missing = 'MISSING',
  PendingCreate = 'PENDING_CREATE',
  Unlinked = 'UNLINKED',
}

export enum DriveFolderMode {
  Create = 'CREATE',
  Link = 'LINK',
}

export enum DriveFolderState {
  Error = 'ERROR',
  Missing = 'MISSING',
  NotSynced = 'NOT_SYNCED',
  Paused = 'PAUSED',
  Pending = 'PENDING',
  Synced = 'SYNCED',
}

export enum DriveImportMode {
  Off = 'OFF',
  Suggest = 'SUGGEST',
  WhenMarked = 'WHEN_MARKED',
}

export enum DriveNewRecordPolicy {
  Ask = 'ASK',
  Auto = 'AUTO',
  Manual = 'MANUAL',
}

export enum DriveOutsideFolderPolicy {
  Ask = 'ASK',
  Move = 'MOVE',
  Never = 'NEVER',
}

export enum DrivePausedReason {
  DriveDisconnected = 'DRIVE_DISCONNECTED',
  ScopeMissing = 'SCOPE_MISSING',
}

export enum DriveProspectConvertedPolicy {
  Ask = 'ASK',
  Leave = 'LEAVE',
  Move = 'MOVE',
}

export enum DriveSubfolderKey {
  Documents = 'DOCUMENTS',
  Email = 'EMAIL',
  Forms = 'FORMS',
  Invoices = 'INVOICES',
  Support = 'SUPPORT',
}

export enum DriveSyncSource {
  EmailAttachments = 'EMAIL_ATTACHMENTS',
  FormUploads = 'FORM_UPLOADS',
  Invoices = 'INVOICES',
  SupportAttachments = 'SUPPORT_ATTACHMENTS',
  Uploads = 'UPLOADS',
}

export enum DriveSyncSourceType {
  CreditNote = 'CREDIT_NOTE',
  CrmUpload = 'CRM_UPLOAD',
  EmailAttachment = 'EMAIL_ATTACHMENT',
  FormUpload = 'FORM_UPLOAD',
  Invoice = 'INVOICE',
  SupportAttachment = 'SUPPORT_ATTACHMENT',
}

export enum DriveSyncStatus {
  Cancelled = 'CANCELLED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  RemovedInDrive = 'REMOVED_IN_DRIVE',
  Skipped = 'SKIPPED',
  SourceRemoved = 'SOURCE_REMOVED',
  Synced = 'SYNCED',
}

/** What happens to the uninvoiced remainder when a client leaves a committed term early. CHARGE_REMAINDER invoices it now as a one-time fee (never a rate, so MRR is untouched); CONTINUE_BILLING keeps the subscription billing period by period to the committed end and ends it there; WAIVE writes it off, recording the figure that was waived. */
export enum EarlyTerminationPolicy {
  ChargeRemainder = 'CHARGE_REMAINDER',
  ContinueBilling = 'CONTINUE_BILLING',
  Waive = 'WAIVE',
}

/** Surface that produced a billing-impacting edit */
export enum EditPath {
  Api = 'API',
  BulkImport = 'BULK_IMPORT',
  ChatDraft = 'CHAT_DRAFT',
  Form = 'FORM',
  System = 'SYSTEM',
  Template = 'TEMPLATE',
}

/** Which incoming email puts a notification in your bell. */
export enum EmailNotificationScope {
  All = 'ALL',
  Clients = 'CLIENTS',
  ClientsAndProspects = 'CLIENTS_AND_PROSPECTS',
  None = 'NONE',
  Prospects = 'PROSPECTS',
}

/** The provider a mailbox is connected through. */
export enum EmailProvider {
  Gmail = 'GMAIL',
  MsGraph = 'MS_GRAPH',
}

/** Whether the signature was authored in Revvly or imported from Gmail. */
export enum EmailSignatureSource {
  Gmail = 'GMAIL',
  Manual = 'MANUAL',
}

/** Status of email delivery */
export enum EmailStatus {
  Bounced = 'BOUNCED',
  Delivered = 'DELIVERED',
  Draft = 'DRAFT',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Received = 'RECEIVED',
  Sent = 'SENT',
}

/** PRIVATE is only the creator; SHARED can be used by anyone on the tenant. */
export enum EmailTemplateVisibility {
  Private = 'PRIVATE',
  Shared = 'SHARED',
}

/** Which triage tier decided a message. */
export enum EmailTriageTier {
  T0 = 'T0',
  T1 = 'T1',
  T2 = 'T2',
}

/** Type/category of email sent */
export enum EmailType {
  Inbound = 'INBOUND',
  Manual = 'MANUAL',
  Notification = 'NOTIFICATION',
  PaymentReminder = 'PAYMENT_REMINDER',
  RenewalReminder = 'RENEWAL_REMINDER',
}

/** Lifecycle status for catalog entities. DRAFT → PUBLISHED (immutable) → ARCHIVED. */
export enum EntityStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
}

/** The kind of platform entity a task relation or CRM subject reference points at (shared by TasksModule and CrmModule). */
export enum EntityTargetType {
  Lead = 'LEAD',
  Organization = 'ORGANIZATION',
  OrganizationContact = 'ORGANIZATION_CONTACT',
}

/** Type of subscription expansion/change */
export enum ExpansionType {
  BundleChange = 'BUNDLE_CHANGE',
  FeatureFlagAdd = 'FEATURE_FLAG_ADD',
  FeatureFlagRemove = 'FEATURE_FLAG_REMOVE',
  FunnelIncrease = 'FUNNEL_INCREASE',
  ModuleAdd = 'MODULE_ADD',
  ModuleRemove = 'MODULE_REMOVE',
  PriceOverride = 'PRICE_OVERRIDE',
  UserIncrease = 'USER_INCREASE',
}

/** Source of how the feature flag was assigned to organization */
export enum FeatureFlagSource {
  Bundle = 'BUNDLE',
  Manual = 'MANUAL',
  Module = 'MODULE',
}

/** Category of stored file */
export enum FileCategory {
  AgentAttachment = 'AGENT_ATTACHMENT',
  Attachment = 'ATTACHMENT',
  Backup = 'BACKUP',
  Document = 'DOCUMENT',
  Export = 'EXPORT',
  FormUpload = 'FORM_UPLOAD',
  Import = 'IMPORT',
  LibraryDocument = 'LIBRARY_DOCUMENT',
  Other = 'OTHER',
  Report = 'REPORT',
  SupportAttachment = 'SUPPORT_ATTACHMENT',
}

/** Status of stored file */
export enum FileStatus {
  Completed = 'COMPLETED',
  Expired = 'EXPIRED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Processing = 'PROCESSING',
}

/** What Intercom recorded for its AI agent (Fin) on a conversation. NONE: Fin did not take part. UNKNOWN: Intercom did not say. ASSUMED_RESOLUTION means the customer did not come back, not that they confirmed. */
export enum FinResolutionState {
  Abandoned = 'ABANDONED',
  AssumedResolution = 'ASSUMED_RESOLUTION',
  ConfirmedResolution = 'CONFIRMED_RESOLUTION',
  None = 'NONE',
  RoutedToTeam = 'ROUTED_TO_TEAM',
  Unknown = 'UNKNOWN',
}

/** Fixed-amount application mode on a component: per unit, or flat lump sum off the line. */
export enum FixedDiscountMode {
  FlatLumpSum = 'FLAT_LUMP_SUM',
  PerUnit = 'PER_UNIT',
}

export enum FormActionFailurePolicy {
  Continue = 'CONTINUE',
  Stop = 'STOP',
}

export enum FormActionRunStatus {
  Failed = 'FAILED',
  Pending = 'PENDING',
  Skipped = 'SKIPPED',
  Succeeded = 'SUCCEEDED',
}

export enum FormActionType {
  CreateLead = 'CREATE_LEAD',
  NotifyUsers = 'NOTIFY_USERS',
  PostSlack = 'POST_SLACK',
  SendEmail = 'SEND_EMAIL',
}

export enum FormClientApplyTarget {
  BillingAddress = 'BILLING_ADDRESS',
  BillingContact = 'BILLING_CONTACT',
  CompanyNumber = 'COMPANY_NUMBER',
  LegalName = 'LEGAL_NAME',
  LegalStructure = 'LEGAL_STRUCTURE',
  SignerContact = 'SIGNER_CONTACT',
  TaxId = 'TAX_ID',
}

export enum FormClientLinkStatus {
  Open = 'OPEN',
  Submitted = 'SUBMITTED',
}

export enum FormEmailSender {
  OrgMailbox = 'ORG_MAILBOX',
  Revvly = 'REVVLY',
  UserMailbox = 'USER_MAILBOX',
}

export enum FormFieldBinding {
  BillingContactEmail = 'BILLING_CONTACT_EMAIL',
  BillingContactName = 'BILLING_CONTACT_NAME',
  BillingContactPhone = 'BILLING_CONTACT_PHONE',
  ClientBillingAddress = 'CLIENT_BILLING_ADDRESS',
  ClientCompanyNumber = 'CLIENT_COMPANY_NUMBER',
  ClientLegalName = 'CLIENT_LEGAL_NAME',
  ClientLegalStructure = 'CLIENT_LEGAL_STRUCTURE',
  ClientTaxId = 'CLIENT_TAX_ID',
  SignerContactEmail = 'SIGNER_CONTACT_EMAIL',
  SignerContactName = 'SIGNER_CONTACT_NAME',
  SignerContactPhone = 'SIGNER_CONTACT_PHONE',
  SubscriptionPeriod = 'SUBSCRIPTION_PERIOD',
  SubscriptionPlan = 'SUBSCRIPTION_PLAN',
  SubscriptionStartDate = 'SUBSCRIPTION_START_DATE',
}

export enum FormFieldRole {
  CompanyName = 'COMPANY_NAME',
  CompanyWebsite = 'COMPANY_WEBSITE',
  ContactEmail = 'CONTACT_EMAIL',
  ContactFirstName = 'CONTACT_FIRST_NAME',
  ContactLastName = 'CONTACT_LAST_NAME',
  ContactName = 'CONTACT_NAME',
  ContactPhone = 'CONTACT_PHONE',
}

export enum FormFieldType {
  Consent = 'CONSENT',
  Date = 'DATE',
  Dropdown = 'DROPDOWN',
  Email = 'EMAIL',
  FileUpload = 'FILE_UPLOAD',
  Heading = 'HEADING',
  Hidden = 'HIDDEN',
  LongText = 'LONG_TEXT',
  MultipleChoice = 'MULTIPLE_CHOICE',
  Number = 'NUMBER',
  Paragraph = 'PARAGRAPH',
  Phone = 'PHONE',
  RichText = 'RICH_TEXT',
  ShortText = 'SHORT_TEXT',
  SingleChoice = 'SINGLE_CHOICE',
  Url = 'URL',
}

export enum FormFileKind {
  Document = 'DOCUMENT',
  Image = 'IMAGE',
  Pdf = 'PDF',
  Presentation = 'PRESENTATION',
  Spreadsheet = 'SPREADSHEET',
  Text = 'TEXT',
}

export enum FormStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
}

export enum FormSubmissionActionStatus {
  AwaitingResponse = 'AWAITING_RESPONSE',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Skipped = 'SKIPPED',
}

export enum FormSubmissionSource {
  Api = 'API',
  ClientLink = 'CLIENT_LINK',
  Custom = 'CUSTOM',
  Embed = 'EMBED',
  Hosted = 'HOSTED',
}

/** A capability within a feature, granted independently of account administration */
export enum FunctionRole {
  CsManager = 'CS_MANAGER',
  CsTeamLead = 'CS_TEAM_LEAD',
  CycleOwner = 'CYCLE_OWNER',
}

/** Whether a role was granted directly or is implied by account administration */
export enum FunctionRoleSource {
  Granted = 'GRANTED',
  ImpliedAdmin = 'IMPLIED_ADMIN',
}

/** A built-in continent, or a region the workspace defined. */
export enum GeoRegionKind {
  Continent = 'CONTINENT',
  Custom = 'CUSTOM',
}

export enum GeoShapeType {
  Box = 'BOX',
  Circle = 'CIRCLE',
}

/** A kind of record Cmd-K searches */
export enum GlobalSearchType {
  Client = 'CLIENT',
  Contact = 'CONTACT',
  Invoice = 'INVOICE',
  Subscription = 'SUBSCRIPTION',
}

/** Which trigger raised a going-dark situation. */
export enum GoingDarkSignal {
  SlowReplies = 'SLOW_REPLIES',
  UnansweredOutbound = 'UNANSWERED_OUTBOUND',
}

/** Google access Revvly can request in a single consent. */
export enum GoogleFeature {
  Calendar = 'CALENDAR',
  Drive = 'DRIVE',
  DriveFolders = 'DRIVE_FOLDERS',
  Gmail = 'GMAIL',
}

export enum HypothesisDispositionAction {
  Carry = 'CARRY',
  Conclude = 'CONCLUDE',
  Drop = 'DROP',
}

export enum HypothesisInconclusiveReason {
  Confounded = 'CONFOUNDED',
  EndedEarly = 'ENDED_EARLY',
  InsufficientData = 'INSUFFICIENT_DATA',
  MeasurementFailed = 'MEASUREMENT_FAILED',
  Other = 'OTHER',
}

export enum HypothesisIssueSeverity {
  Block = 'BLOCK',
  Confirm = 'CONFIRM',
  Inform = 'INFORM',
}

export enum HypothesisLinkSource {
  Clickup = 'CLICKUP',
  Manual = 'MANUAL',
}

export enum HypothesisNextStep {
  Iterate = 'ITERATE',
  None = 'NONE',
  Retest = 'RETEST',
  RollOut = 'ROLL_OUT',
}

export enum HypothesisOutcome {
  Inconclusive = 'INCONCLUSIVE',
  Invalidated = 'INVALIDATED',
  Validated = 'VALIDATED',
}

export enum HypothesisSnapshotKind {
  End = 'END',
  Start = 'START',
}

export enum HypothesisSnapshotSkip {
  MetricChanged = 'METRIC_CHANGED',
  MetricWithdrawn = 'METRIC_WITHDRAWN',
  ReadFailed = 'READ_FAILED',
}

export enum HypothesisStatus {
  Backlog = 'BACKLOG',
  Carried = 'CARRIED',
  Concluded = 'CONCLUDED',
  Developing = 'DEVELOPING',
  Dropped = 'DROPPED',
  Testing = 'TESTING',
}

/** Whether an import run still stands or has been rolled back */
export enum ImportBatchStatus {
  Completed = 'COMPLETED',
  RolledBack = 'ROLLED_BACK',
}

export enum InboxActionStatus {
  Applied = 'APPLIED',
  Failed = 'FAILED',
  Undone = 'UNDONE',
}

export enum InboxCategory {
  Bills = 'BILLS',
  Calendar = 'CALENDAR',
  Client = 'CLIENT',
  Hr = 'HR',
  Legal = 'LEGAL',
  Newsletters = 'NEWSLETTERS',
  NewContact = 'NEW_CONTACT',
  Payments = 'PAYMENTS',
  Personal = 'PERSONAL',
  Promotions = 'PROMOTIONS',
  Prospect = 'PROSPECT',
  Receivables = 'RECEIVABLES',
  Security = 'SECURITY',
  Team = 'TEAM',
  Tools = 'TOOLS',
  Unsorted = 'UNSORTED',
}

export enum InboxCategorySource {
  LearnedProfile = 'LEARNED_PROFILE',
  Lexical = 'LEXICAL',
  Model = 'MODEL',
  Neighbours = 'NEIGHBOURS',
  Rule = 'RULE',
  User = 'USER',
  UserRule = 'USER_RULE',
}

export enum InboxLearnedSenderKind {
  Address = 'ADDRESS',
  Domain = 'DOMAIN',
}

export enum InboxMoveFollowUpKind {
  Conflict = 'CONFLICT',
  None = 'NONE',
  OfferRule = 'OFFER_RULE',
}

export enum InboxPlacement {
  Other = 'OTHER',
  Primary = 'PRIMARY',
  Skip = 'SKIP',
}

export enum InboxPolicyAction {
  AgingAlert = 'AGING_ALERT',
  Archive = 'ARCHIVE',
  DraftReply = 'DRAFT_REPLY',
  MarkRead = 'MARK_READ',
  MoveToOther = 'MOVE_TO_OTHER',
}

export enum InboxRuleField {
  OwnDomain = 'OWN_DOMAIN',
  SenderAddress = 'SENDER_ADDRESS',
  SenderDomain = 'SENDER_DOMAIN',
  SubjectContains = 'SUBJECT_CONTAINS',
}

export enum InboxRuleScope {
  Address = 'ADDRESS',
  Domain = 'DOMAIN',
}

export enum InboxTab {
  Done = 'DONE',
  NeedsReply = 'NEEDS_REPLY',
  Other = 'OTHER',
  Primary = 'PRIMARY',
  Snoozed = 'SNOOZED',
}

export enum InboxThreadState {
  Done = 'DONE',
  Open = 'OPEN',
  Snoozed = 'SNOOZED',
}

/** Where the Intercom workspace is hosted: api.intercom.io, EU or AU. */
export enum IntercomRegion {
  Au = 'AU',
  Eu = 'EU',
  Us = 'US',
}

export enum InviteApprovalStatus {
  Approved = 'APPROVED',
  Declined = 'DECLINED',
  Pending = 'PENDING',
}

/** How a subscription's invoices are treated on delete: LEAVE keeps them, VOID marks them CANCELLED (kept for audit), DELETE permanently removes them (mistake correction). */
export enum InvoiceDeletionMode {
  Delete = 'DELETE',
  Leave = 'LEAVE',
  Void = 'VOID',
}

/** The identity an invoice email is sent from. */
export enum InvoiceEmailSender {
  AccountingMailbox = 'ACCOUNTING_MAILBOX',
  Platform = 'PLATFORM',
  UserMailbox = 'USER_MAILBOX',
}

/** Issuance state, independent of payment: DRAFT (created, not issued, no number or dates), ISSUED (number and dates stamped), VOID (abandoned; drafts are voided, never deleted). */
export enum InvoiceIssueState {
  Draft = 'DRAFT',
  Issued = 'ISSUED',
  Void = 'VOID',
}

/** What an invoice line represents: a plan charge (BASE, or BASE_SPLIT for one share of one), metered usage (VARIABLE), an included-units display line (FREE_INCLUSION), a mid-cycle adjustment (PRORATION), a one-off charge (ONE_TIME_FEE), or a credit-note line (CREDIT). */
export enum InvoiceLineKind {
  Base = 'BASE',
  BaseSplit = 'BASE_SPLIT',
  Credit = 'CREDIT',
  FreeInclusion = 'FREE_INCLUSION',
  OneTimeFee = 'ONE_TIME_FEE',
  Proration = 'PRORATION',
  Variable = 'VARIABLE',
}

/** Commercial meaning of a line on an imported invoice */
export enum InvoiceLineType {
  BaseRecurring = 'BASE_RECURRING',
  Credit = 'CREDIT',
  Discount = 'DISCOUNT',
  Informational = 'INFORMATIONAL',
  OneTime = 'ONE_TIME',
  Tax = 'TAX',
  Unknown = 'UNKNOWN',
  Usage = 'USAGE',
}

/** Where a recorded payment came from — the inbound payment webhook, or an operator settling the invoice in Revvly. */
export enum InvoicePaymentSource {
  CreditNote = 'CREDIT_NOTE',
  Manual = 'MANUAL',
  Stripe = 'STRIPE',
  Webhook = 'WEBHOOK',
}

/** Status of the system-generated PDF attachment for an invoice */
export enum InvoicePdfStatus {
  Failed = 'FAILED',
  Pending = 'PENDING',
  Ready = 'READY',
}

/** How a subscription's invoices are handled when its start date changes and the billing series is regenerated: REBUILD_ALL deletes and rebuilds everything, PRESERVE_PAID keeps paid invoices and only rebuilds the unpaid/uncovered periods, VOID_AND_REISSUE marks the old invoices CANCELLED (audit trail) and issues a fresh series. */
export enum InvoiceRegenerationMode {
  PreservePaid = 'PRESERVE_PAID',
  RebuildAll = 'REBUILD_ALL',
  VoidAndReissue = 'VOID_AND_REISSUE',
}

/** How the invoice was created */
export enum InvoiceSource {
  ExcelImport = 'EXCEL_IMPORT',
  GoogleDrive = 'GOOGLE_DRIVE',
  Manual = 'MANUAL',
  System = 'SYSTEM',
}

/** Type of invoice (subscription, expansion, service, etc.) */
export enum InvoiceType {
  Discount = 'DISCOUNT',
  Expansion = 'EXPANSION',
  Overage = 'OVERAGE',
  Refund = 'REFUND',
  Renewal = 'RENEWAL',
  Service = 'SERVICE',
  Subscription = 'SUBSCRIPTION',
}

export enum KbArticleSortField {
  Path = 'PATH',
  Source = 'SOURCE',
  Status = 'STATUS',
  Title = 'TITLE',
  Updated = 'UPDATED',
}

export enum KbArticleStatus {
  Excluded = 'EXCLUDED',
  Failed = 'FAILED',
  Indexed = 'INDEXED',
  NeedsReview = 'NEEDS_REVIEW',
}

export enum KbAudienceSource {
  Inferred = 'INFERRED',
  Override = 'OVERRIDE',
  Rule = 'RULE',
}

export enum KbConfidence {
  High = 'HIGH',
  Low = 'LOW',
  Medium = 'MEDIUM',
}

/** How an audience rule's text is compared with an article's address path. */
export enum KbPathMatch {
  Contains = 'CONTAINS',
  EndsWith = 'ENDS_WITH',
  Is = 'IS',
  IsNot = 'IS_NOT',
  NotContains = 'NOT_CONTAINS',
  NotEndsWith = 'NOT_ENDS_WITH',
  NotStartsWith = 'NOT_STARTS_WITH',
  StartsWith = 'STARTS_WITH',
}

export enum KbSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

/** Which way a Key Result is meant to move. */
export enum KeyResultDirection {
  Decrease = 'DECREASE',
  Increase = 'INCREASE',
}

export enum KeyResultPaceStatus {
  Ahead = 'AHEAD',
  Behind = 'BEHIND',
  OnPace = 'ON_PACE',
}

export enum KeyResultSource {
  Manual = 'MANUAL',
  Metric = 'METRIC',
}

export enum KeyResultStatus {
  Dropped = 'DROPPED',
  Open = 'OPEN',
  Scored = 'SCORED',
}

export enum KeyResultUnit {
  Count = 'COUNT',
  Currency = 'CURRENCY',
  Percent = 'PERCENT',
}

export enum KnowledgeSchedule {
  Daily = 'DAILY',
  Hourly = 'HOURLY',
  Manual = 'MANUAL',
  Weekly = 'WEEKLY',
}

export enum KnowledgeSourceKind {
  Api = 'API',
  Clickup = 'CLICKUP',
  Crawl = 'CRAWL',
  Intercom = 'INTERCOM',
  Upload = 'UPLOAD',
  Zendesk = 'ZENDESK',
}

export enum KnowledgeSourceStatus {
  Crawling = 'CRAWLING',
  Disabled = 'DISABLED',
  Failed = 'FAILED',
  Idle = 'IDLE',
}

/** How a CRM relationship record was created. */
export enum LeadSource {
  AiSignal = 'AI_SIGNAL',
  Api = 'API',
  Booking = 'BOOKING',
  Form = 'FORM',
  Import = 'IMPORT',
  InboundEmail = 'INBOUND_EMAIL',
  Manual = 'MANUAL',
  Referral = 'REFERRAL',
}

/** CRM lead/deal status. */
export enum LeadStatus {
  Lost = 'LOST',
  Open = 'OPEN',
  Won = 'WON',
}

/** What kind of document it is, for filtering the library. */
export enum LibraryDocumentCategory {
  CaseStudy = 'CASE_STUDY',
  Contract = 'CONTRACT',
  Deck = 'DECK',
  Guide = 'GUIDE',
  Other = 'OTHER',
  Pricing = 'PRICING',
  Proposal = 'PROPOSAL',
}

/** Anchor for the limit period reset */
export enum LimitPeriodAlignment {
  CalendarUtc = 'CALENDAR_UTC',
  CustomerTimezone = 'CUSTOMER_TIMEZONE',
  FirstUse = 'FIRST_USE',
  SubscriptionAnchor = 'SUBSCRIPTION_ANCHOR',
}

/** How an inclusion/allowance refreshes within the billing cycle */
export enum LimitPeriodType {
  BillingCycle = 'BILLING_CYCLE',
  FixedCycle = 'FIXED_CYCLE',
  RollingWindow = 'ROLLING_WINDOW',
}

/** How leftover inclusions/allowances are handled when a limit period resets */
export enum LimitPeriodUnusedBehavior {
  Forfeit = 'FORFEIT',
  Rollover = 'ROLLOVER',
}

/** Why an onboarding left the assistant for the setup wizard */
export enum ManualFallbackReason {
  Chosen = 'CHOSEN',
  ModelError = 'MODEL_ERROR',
}

export enum McpRemoteAccess {
  Admins = 'ADMINS',
  Members = 'MEMBERS',
}

export enum McpRemoteAuthType {
  Bearer = 'BEARER',
  Header = 'HEADER',
  None = 'NONE',
  Oauth = 'OAUTH',
}

export enum McpRemoteOAuthStatus {
  Connected = 'CONNECTED',
  NeedsReauth = 'NEEDS_REAUTH',
  Pending = 'PENDING',
}

/** Peak (reserved capacity) vs Cumulative (consumption) measurement */
export enum MeasurementMode {
  Cumulative = 'CUMULATIVE',
  Peak = 'PEAK',
}

export enum MemberInviteJoinState {
  Accepted = 'ACCEPTED',
  Expired = 'EXPIRED',
  NotFound = 'NOT_FOUND',
  Pending = 'PENDING',
  Revoked = 'REVOKED',
}

export enum MemberInviteStatus {
  Accepted = 'ACCEPTED',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Revoked = 'REVOKED',
}

/** Message-level triage verdict. */
export enum MessageTriageVerdict {
  Pending = 'PENDING',
  Qualified = 'QUALIFIED',
  Rejected = 'REJECTED',
}

/** Type of metric for monthly drill-down analysis */
export enum MetricType {
  Acquisition = 'ACQUISITION',
  Arr = 'ARR',
  Churn = 'CHURN',
  Contraction = 'CONTRACTION',
  Expansion = 'EXPANSION',
  Mrr = 'MRR',
  Nrr = 'NRR',
  Reactivation = 'REACTIVATION',
  Renewal = 'RENEWAL',
}

/** Which kind of pin a bulk migration is rewriting. */
export enum MigrationKind {
  Bundle = 'BUNDLE',
  Component = 'COMPONENT',
  Module = 'MODULE',
}

/** When a subscription migration takes effect. */
export enum MigrationTiming {
  Backdated = 'BACKDATED',
  MidCycleProrated = 'MID_CYCLE_PRORATED',
  NextRenewal = 'NEXT_RENEWAL',
}

/** Recommended Y-axis scaling for the MRR movement chart (SHARED = bars and trend line on one axis; SEPARATE = bars on a zoomed secondary axis). */
export enum MrrAxisScaleMode {
  Separate = 'SEPARATE',
  Shared = 'SHARED',
}

export enum NotificationCategory {
  Account = 'ACCOUNT',
  Assignment = 'ASSIGNMENT',
  Checkin = 'CHECKIN',
  CycleState = 'CYCLE_STATE',
  Digest = 'DIGEST',
  TargetChange = 'TARGET_CHANGE',
  Team = 'TEAM',
}

/** What a notification-center item is about. */
export enum NotificationCenterKind {
  CheckinReportFailed = 'CHECKIN_REPORT_FAILED',
  CheckinSlackPaused = 'CHECKIN_SLACK_PAUSED',
  CrmMeetingBooked = 'CRM_MEETING_BOOKED',
  DriveFoldersPaused = 'DRIVE_FOLDERS_PAUSED',
  EmailReply = 'EMAIL_REPLY',
  EmailSendFailed = 'EMAIL_SEND_FAILED',
  FormActionsFailed = 'FORM_ACTIONS_FAILED',
  FormSubmission = 'FORM_SUBMISSION',
  ImportReviewReady = 'IMPORT_REVIEW_READY',
  InboxDigest = 'INBOX_DIGEST',
  InvoiceOverdue = 'INVOICE_OVERDUE',
  OkrKeyResultAssigned = 'OKR_KEY_RESULT_ASSIGNED',
  OkrObjectiveAssigned = 'OKR_OBJECTIVE_ASSIGNED',
  OkrScoringOpened = 'OKR_SCORING_OPENED',
  OkrTargetChanged = 'OKR_TARGET_CHANGED',
  TaskReminder = 'TASK_REMINDER',
  UsageZeroPending = 'USAGE_ZERO_PENDING',
}

export enum NotificationPriority {
  Digest = 'DIGEST',
  Immediate = 'IMMEDIATE',
}

/** The part of Revvly a notification came from. */
export enum NotificationSource {
  Billing = 'BILLING',
  Checkins = 'CHECKINS',
  Crm = 'CRM',
  Email = 'EMAIL',
  Forms = 'FORMS',
  Integrations = 'INTEGRATIONS',
  Okr = 'OKR',
  Tasks = 'TASKS',
}

export enum ObjectiveLevel {
  Company = 'COMPANY',
  Team = 'TEAM',
}

export enum ObjectiveType {
  Aspirational = 'ASPIRATIONAL',
  Committed = 'COMMITTED',
}

/** Direction of a relative anchor offset. */
export enum OffsetDirection {
  After = 'AFTER',
  Before = 'BEFORE',
}

export enum OkrCoachRole {
  Author = 'AUTHOR',
  Coach = 'COACH',
}

export enum OkrDraftVerdict {
  CouldBeStronger = 'COULD_BE_STRONGER',
  NeedsWork = 'NEEDS_WORK',
  Strong = 'STRONG',
}

/** A Revvly metric a Key Result can be bound to instead of a manual check-in. */
export enum OkrMetricKey {
  ActiveSubscriptions = 'ACTIVE_SUBSCRIPTIONS',
  Arr = 'ARR',
  DealsCreatedCycle = 'DEALS_CREATED_CYCLE',
  ExpansionMrrMonthly = 'EXPANSION_MRR_MONTHLY',
  LeadsCreatedCycle = 'LEADS_CREATED_CYCLE',
  LeadsCreatedMonthly = 'LEADS_CREATED_MONTHLY',
  MeteredUsageRevenueMonthly = 'METERED_USAGE_REVENUE_MONTHLY',
  Mrr = 'MRR',
  NewAccountsCycle = 'NEW_ACCOUNTS_CYCLE',
  NewAccountsMonthly = 'NEW_ACCOUNTS_MONTHLY',
  Nrr = 'NRR',
  ReceivablesOver_60Days = 'RECEIVABLES_OVER_60_DAYS',
  RenewedAccountsCycle = 'RENEWED_ACCOUNTS_CYCLE',
  RenewedAccountsExclMonthlyCycle = 'RENEWED_ACCOUNTS_EXCL_MONTHLY_CYCLE',
  RenewedAccountsExclMonthlyMonthly = 'RENEWED_ACCOUNTS_EXCL_MONTHLY_MONTHLY',
  RenewedAccountsMonthly = 'RENEWED_ACCOUNTS_MONTHLY',
  RevenueChurnMonthly = 'REVENUE_CHURN_MONTHLY',
}

export enum OkrWorkLinkSource {
  Clickup = 'CLICKUP',
}

/** Status of tenant onboarding process */
export enum OnboardingStatus {
  Completed = 'COMPLETED',
  InProgress = 'IN_PROGRESS',
  NotStarted = 'NOT_STARTED',
}

/** Category of an onboarding step */
export enum OnboardingStepCategory {
  Optional = 'OPTIONAL',
  Recommended = 'RECOMMENDED',
  Required = 'REQUIRED',
}

export enum OnboardingTourAction {
  Back = 'BACK',
  Goto = 'GOTO',
  Next = 'NEXT',
  Pause = 'PAUSE',
  Resume = 'RESUME',
  Skip = 'SKIP',
}

/** Where a tour step stands; a step left without its action is VIEWED, not DONE */
export enum OnboardingTourStepStatus {
  Done = 'DONE',
  Pending = 'PENDING',
  Skipped = 'SKIPPED',
  Viewed = 'VIEWED',
}

export enum OutboundMessageStatus {
  Collapsed = 'COLLAPSED',
  Failed = 'FAILED',
  Queued = 'QUEUED',
  Sent = 'SENT',
  SkippedMuted = 'SKIPPED_MUTED',
  SkippedSuppressed = 'SKIPPED_SUPPRESSED',
  SkippedUnmapped = 'SKIPPED_UNMAPPED',
}

export enum OutboundWebhookDeliveryStatus {
  Cancelled = 'CANCELLED',
  Delivered = 'DELIVERED',
  Failed = 'FAILED',
  Pending = 'PENDING',
}

/** One of the fixed payment reminder emails the product sends. */
export enum PaymentReminderTemplateKind {
  AutoDueToday = 'AUTO_DUE_TODAY',
  AutoOverdue_1 = 'AUTO_OVERDUE_1',
  AutoOverdue_7 = 'AUTO_OVERDUE_7',
  AutoOverdue_14 = 'AUTO_OVERDUE_14',
  AutoOverdue_30 = 'AUTO_OVERDUE_30',
  AutoUpcoming = 'AUTO_UPCOMING',
  Final = 'FINAL',
  Friendly = 'FRIENDLY',
  Invoice = 'INVOICE',
  Overdue = 'OVERDUE',
}

/** MANUAL is offered in the send composer; AUTOMATIC is sent by the daily sweep. */
export enum PaymentReminderTrigger {
  Automatic = 'AUTOMATIC',
  Manual = 'MANUAL',
}

/** Payment status */
export enum PaymentStatus {
  Cancelled = 'CANCELLED',
  Failed = 'FAILED',
  Overdue = 'OVERDUE',
  Paid = 'PAID',
  Pending = 'PENDING',
}

/** Pipeline stage kind (drives lead status). */
export enum PipelineStageKind {
  Lost = 'LOST',
  Open = 'OPEN',
  Won = 'WON',
}

export enum PlanOptions {
  Enterprise = 'enterprise',
  Essential = 'essential',
  Premium = 'premium',
  Starter = 'starter',
  StarterPlus = 'starterPlus',
}

/** DEGRADED and MAINTENANCE show amber, DOWN shows red, on the Revvly icon. */
export enum PlatformIncidentSeverity {
  Degraded = 'DEGRADED',
  Down = 'DOWN',
  Maintenance = 'MAINTENANCE',
}

/** A legal policy document a user can accept (Terms, Privacy, Cookies). */
export enum PolicyType {
  Cookies = 'COOKIES',
  Privacy = 'PRIVACY',
  Terms = 'TERMS',
}

/** What to do about the money when a plan changes inside a prepaid span. CHARGE_NOW raises one catch-up invoice for the price difference across the periods the prepayment still covers; NO_CHARGE applies the change and bills nothing more until the span runs out. The plan changes immediately either way, and MRR moves with it — this decides cash only. */
export enum PrepaidSpanChange {
  ChargeNow = 'CHARGE_NOW',
  NoCharge = 'NO_CHARGE',
}

/** What a prepaid term's prepay incentive discounts: the BASE price, the standing COMPONENTS paid ahead with it, or BOTH. Components that reset each period are never prepaid. */
export enum PrepayDiscountScope {
  Base = 'BASE',
  Both = 'BOTH',
  Components = 'COMPONENTS',
}

/** When a per-subscription price override takes effect. */
export enum PriceEffectiveMode {
  ImmediateProrated = 'IMMEDIATE_PRORATED',
  Prospective = 'PROSPECTIVE',
}

/** Pricing element a per-subscription override targets. */
export enum PriceOverrideElementType {
  BasePrice = 'BASE_PRICE',
  ComponentRate = 'COMPONENT_RATE',
}

/** Type of pricing combination (bundle or module) */
export enum PricingCombinationType {
  Bundle = 'BUNDLE',
  Module = 'MODULE',
}

/** Status of a pending draft — either awaiting review or expired past its 24h TTL. */
export enum PricingDraftSummaryStatus {
  Expired = 'EXPIRED',
  Pending = 'PENDING',
}

/** Operator to combine multiple pricing filters */
export enum PricingFilterOperator {
  And = 'AND',
  Or = 'OR',
}

/** Type of pricing filter to apply */
export enum PricingFilterType {
  HasGlobal = 'HAS_GLOBAL',
  HasRegional = 'HAS_REGIONAL',
  MissingGlobal = 'MISSING_GLOBAL',
  MissingRegional = 'MISSING_REGIONAL',
}

/** Pricing model for a Variable Component (PRD §5) */
export enum PricingModelType {
  Commission = 'COMMISSION',
  CommittedOverage = 'COMMITTED_OVERAGE',
  FlatRate = 'FLAT_RATE',
  GraduatedTiered = 'GRADUATED_TIERED',
  Promotional = 'PROMOTIONAL',
  Stairstep = 'STAIRSTEP',
  TrueOverage = 'TRUE_OVERAGE',
  VolumeTiered = 'VOLUME_TIERED',
}

/** Type of entity that the pricing rule references */
export enum PricingRuleRefType {
  Bundle = 'BUNDLE',
  Feature = 'FEATURE',
  Module = 'MODULE',
  Plan = 'PLAN',
}

/** Type of pricing rule */
export enum PricingRuleType {
  BasePrice = 'BASE_PRICE',
  Discount = 'DISCOUNT',
  Tax = 'TAX',
  VariableItem = 'VARIABLE_ITEM',
}

/** Scope of pricing rule application */
export enum PricingScope {
  Global = 'GLOBAL',
  Organization = 'ORGANIZATION',
  Region = 'REGION',
}

/** How far one catalog item is trusted to match without review */
export enum ProductAutonomyStatus {
  Earned = 'EARNED',
  Learning = 'LEARNING',
  NeedsAttention = 'NEEDS_ATTENTION',
}

/** Who made a mapping. */
export enum ProductEventMappingSource {
  Confirmed = 'CONFIRMED',
  Suggested = 'SUGGESTED',
}

/** What an event type can be tied to. */
export enum ProductEventTargetKind {
  Bundle = 'BUNDLE',
  FeatureFlag = 'FEATURE_FLAG',
  Module = 'MODULE',
}

/** Where an event type stands in the event inbox. */
export enum ProductEventTypeStatus {
  Ignored = 'IGNORED',
  Mapped = 'MAPPED',
  New = 'NEW',
}

/** Status of AI product matching for a subscription */
export enum ProductMatchStatus {
  Manual = 'MANUAL',
  Matched = 'MATCHED',
  Unmatched = 'UNMATCHED',
}

/** A sellable catalog product priced per market: a Module or a Bundle. */
export enum ProductType {
  Bundle = 'BUNDLE',
  Module = 'MODULE',
}

/** The size of a bucket in a usage trend. Weeks start on Monday; all of it is UTC. */
export enum ProductUsageInterval {
  Day = 'DAY',
  Month = 'MONTH',
  Week = 'WEEK',
}

/** What a usage trend counts: every event, or the distinct users who fired it. */
export enum ProductUsageMeasure {
  EventCount = 'EVENT_COUNT',
  Users = 'USERS',
}

/** Confidence band for a billing projection (PRD §17.1) */
export enum ProjectionConfidence {
  High = 'HIGH',
  Low = 'LOW',
  Medium = 'MEDIUM',
}

/** Which subscription elements a promotion applies to */
export enum PromotionalAppliesTo {
  BaseOnly = 'BASE_ONLY',
  Both = 'BOTH',
  VariableOnly = 'VARIABLE_ONLY',
}

/** Promotional discount type (percentage or fixed currency amount) */
export enum PromotionalDiscountType {
  FixedAmount = 'FIXED_AMOUNT',
  Percentage = 'PERCENTAGE',
}

/** When a promotional discount starts */
export enum PromotionalStartTrigger {
  ExplicitDate = 'EXPLICIT_DATE',
  SubscriptionStart = 'SUBSCRIPTION_START',
}

/** How a mid-cycle quantity change is prorated on the invoice */
export enum ProrationCalculation {
  Daily = 'DAILY',
  FullMonth = 'FULL_MONTH',
  None = 'NONE',
}

/** Coarse public GraphQL capability. READ is always granted; WRITE is required for mutations (createClient, createProspect, …); SUPPORT_TOKEN_MINT lets the key mint Revvly Support end-user tokens; FORM_SUBMIT lets it submit to published forms (submitForm); KNOWLEDGE_WRITE lets it push help articles; USAGE_WRITE lets it report metered usage and events over REST; PAYMENT_WRITE lets it record invoice payments over the inbound payment webhook; PRODUCT_EVENT_WRITE lets it send product usage events over REST. */
export enum PublicApiCapability {
  FormSubmit = 'FORM_SUBMIT',
  KnowledgeWrite = 'KNOWLEDGE_WRITE',
  PaymentWrite = 'PAYMENT_WRITE',
  ProductEventWrite = 'PRODUCT_EVENT_WRITE',
  Read = 'READ',
  SupportTokenMint = 'SUPPORT_TOKEN_MINT',
  UsageWrite = 'USAGE_WRITE',
  Write = 'WRITE',
}

/** Catalog entity type a publish-gap check is run against. */
export enum PublishGapEntityType {
  Bundle = 'BUNDLE',
  Module = 'MODULE',
}

/** How a billing-impacting change was authored */
export enum PublishPath {
  Api = 'API',
  ChatDraft = 'CHAT_DRAFT',
  Form = 'FORM',
  Template = 'TEMPLATE',
}

/** Recommended action for a pending renewal */
export enum RecommendedAction {
  ContactCustomer = 'CONTACT_CUSTOMER',
  Expire = 'EXPIRE',
  Renew = 'RENEW',
}

/** How often a recurring task series generates a new task. */
export enum RecurrenceFrequency {
  Daily = 'DAILY',
  Monthly = 'MONTHLY',
  Weekly = 'WEEKLY',
  Yearly = 'YEARLY',
}

/** Weekday selector for weekly recurrences (0 = Sunday .. 6 = Saturday). */
export enum RecurrenceWeekday {
  Friday = 'FRIDAY',
  Monday = 'MONDAY',
  Saturday = 'SATURDAY',
  Sunday = 'SUNDAY',
  Thursday = 'THURSDAY',
  Tuesday = 'TUESDAY',
  Wednesday = 'WEDNESDAY',
}

export enum ReleaseKind {
  Major = 'MAJOR',
  Minor = 'MINOR',
  Patch = 'PATCH',
}

/** Type of payment reminder based on due date offset */
export enum ReminderType {
  DueToday = 'DUE_TODAY',
  Overdue_1 = 'OVERDUE_1',
  Overdue_7 = 'OVERDUE_7',
  Overdue_14 = 'OVERDUE_14',
  Overdue_30 = 'OVERDUE_30',
  Upcoming = 'UPCOMING',
}

/** Status of a renewal action in the queue */
export enum RenewalActionStatus {
  Approved = 'APPROVED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Processed = 'PROCESSED',
  Rejected = 'REJECTED',
}

/** Type of renewal date filter comparison */
export enum RenewalDateFilterType {
  Equal = 'EQUAL',
  GreaterThan = 'GREATER_THAN',
  LessThan = 'LESS_THAN',
}

/** Renewal status indicating future subscription state */
export enum RenewalStatus {
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
  GracePeriod = 'GRACE_PERIOD',
  PendingDecision = 'PENDING_DECISION',
  WillCancel = 'WILL_CANCEL',
  WillRenew = 'WILL_RENEW',
}

/** What happens to the quantity when the period closes: STANDING carries it forward, PER_PERIOD resets it to zero. */
export enum ResetBehaviour {
  PerPeriod = 'PER_PERIOD',
  Standing = 'STANDING',
}

/** Why an invoice is in the review queue at this position */
export enum ReviewReason {
  Coverage = 'COVERAGE',
  Depth = 'DEPTH',
  Disagreement = 'DISAGREEMENT',
  Novelty = 'NOVELTY',
}

/** Who the company sells to: businesses, consumers, government, or as a marketplace */
export enum SalesModel {
  B2B = 'B2B',
  B2B2C = 'B2B2C',
  B2C = 'B2C',
  B2G = 'B2G',
  Marketplace = 'MARKETPLACE',
}

/** The named shape a billing schedule was built from. v1 exposes DEFERRED_ACTIVATION and PREPAID_TERM; CUSTOM covers directly-supplied charges. */
export enum SchedulePreset {
  Custom = 'CUSTOM',
  DeferredActivation = 'DEFERRED_ACTIVATION',
  PrepaidTerm = 'PREPAID_TERM',
}

/** Who caused a schedule trigger to fire: a dashboard user, a scheduled job, or the API. */
export enum ScheduleTriggerActor {
  Api = 'API',
  System = 'SYSTEM',
  TenantUser = 'TENANT_USER',
}

/** What an entry in the append-only schedule trigger log records. PERIOD_COVERED lives here rather than in the lifecycle log because a covered period moves no MRR. */
export enum ScheduleTriggerEventType {
  Activated = 'ACTIVATED',
  DeadlineMissed = 'DEADLINE_MISSED',
  NoopTrigger = 'NOOP_TRIGGER',
  PeriodCovered = 'PERIOD_COVERED',
  ScheduleAttached = 'SCHEDULE_ATTACHED',
  ScheduleCancelled = 'SCHEDULE_CANCELLED',
  ScheduleCompleted = 'SCHEDULE_COMPLETED',
  TriggerFired = 'TRIGGER_FIRED',
}

/** A scheduled job that can be run on demand outside production. */
export enum ScheduledJobKey {
  ClickupChatPoll = 'CLICKUP_CHAT_POLL',
  InviteExpireSweep = 'INVITE_EXPIRE_SWEEP',
  InviteReminderSweep = 'INVITE_REMINDER_SWEEP',
  NotificationsDigestSweep = 'NOTIFICATIONS_DIGEST_SWEEP',
  OkrCheckinReminder = 'OKR_CHECKIN_REMINDER',
  OkrHypothesisReminder = 'OKR_HYPOTHESIS_REMINDER',
  OkrMetricRefresh = 'OKR_METRIC_REFRESH',
  OkrScoringReminder = 'OKR_SCORING_REMINDER',
  OkrWeeklyDigest = 'OKR_WEEKLY_DIGEST',
  OkrWeeklyPrompt = 'OKR_WEEKLY_PROMPT',
  OkrWorkLinkSync = 'OKR_WORK_LINK_SYNC',
  SlackHealthCheck = 'SLACK_HEALTH_CHECK',
}

/** Per-level outcome from the price resolution walk. */
export enum ScopeWalkState {
  Applied = 'APPLIED',
  Fallback = 'FALLBACK',
  NoMatch = 'NO_MATCH',
  Skipped = 'SKIPPED',
}

/** A group of fields a search can be narrowed to */
export enum SearchField {
  Domain = 'DOMAIN',
  Email = 'EMAIL',
  InvoiceNumber = 'INVOICE_NUMBER',
  Name = 'NAME',
  Notes = 'NOTES',
  Phone = 'PHONE',
  Reference = 'REFERENCE',
  TaxId = 'TAX_ID',
}

export enum SeatEventSource {
  AddUser = 'ADD_USER',
  Deactivation = 'DEACTIVATION',
  InviteEmail = 'INVITE_EMAIL',
  InviteSlack = 'INVITE_SLACK',
  Master = 'MASTER',
}

export enum SeatEventType {
  LimitChanged = 'LIMIT_CHANGED',
  PriceChanged = 'PRICE_CHANGED',
  SeatConsumed = 'SEAT_CONSUMED',
  SeatReleased = 'SEAT_RELEASED',
}

export enum SeatRequestStatus {
  Declined = 'DECLINED',
  Fulfilled = 'FULFILLED',
  Open = 'OPEN',
}

export enum SignedCopyStatus {
  Failed = 'FAILED',
  Pending = 'PENDING',
  Sent = 'SENT',
}

export enum SigningOrder {
  Parallel = 'PARALLEL',
  Sequential = 'SEQUENTIAL',
}

export enum SlackConnectionStatus {
  Connected = 'CONNECTED',
  Degraded = 'DEGRADED',
  Disconnected = 'DISCONNECTED',
  Revoked = 'REVOKED',
  Uninstalled = 'UNINSTALLED',
}

export enum SlackMappingSource {
  EmailMatch = 'EMAIL_MATCH',
  Invite = 'INVITE',
  Manual = 'MANUAL',
  SelfLinked = 'SELF_LINKED',
}

export enum SlackReachability {
  Deactivated = 'DEACTIVATED',
  Mapped = 'MAPPED',
  Unmapped = 'UNMAPPED',
}

/** Sort order for paginated queries */
export enum SortOrder {
  Asc = 'ASC',
  Desc = 'DESC',
}

/** Which pin on a subscription is stale: module, bundle, or component. */
export enum StalePinType {
  Bundle = 'BUNDLE',
  Component = 'COMPONENT',
  Module = 'MODULE',
}

/** The four fixed categories every tenant-defined status maps to. */
export enum StatusCategory {
  Closed = 'CLOSED',
  InProgress = 'IN_PROGRESS',
  Open = 'OPEN',
  Pending = 'PENDING',
}

/** What a client is owed before a new sub-processor starts. */
export enum SubprocessorConsent {
  Notice = 'NOTICE',
  Objection = 'OBJECTION',
  PriorConsent = 'PRIOR_CONSENT',
}

/** Kind of subscription lifecycle transition. Analytics derives ARR/MRR/churn/expansion from these events. */
export enum SubscriptionLifecycleEventType {
  AddComponent = 'ADD_COMPONENT',
  CancelScheduled = 'CANCEL_SCHEDULED',
  Churned = 'CHURNED',
  Contracted = 'CONTRACTED',
  Created = 'CREATED',
  Expanded = 'EXPANDED',
  MigrateBundle = 'MIGRATE_BUNDLE',
  MigrateComponent = 'MIGRATE_COMPONENT',
  MigrateModule = 'MIGRATE_MODULE',
  Reactivated = 'REACTIVATED',
  RemoveComponent = 'REMOVE_COMPONENT',
  Renewed = 'RENEWED',
}

/** Subscription billing period */
export enum SubscriptionPeriod {
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
  Yearly = 'YEARLY',
}

/** Available fields for sorting subscriptions */
export enum SubscriptionSortField {
  BasePrice = 'BASE_PRICE',
  CreatedAt = 'CREATED_AT',
  EndDate = 'END_DATE',
  NextRenewalDate = 'NEXT_RENEWAL_DATE',
  OrganizationId = 'ORGANIZATION_ID',
  StartDate = 'START_DATE',
  UpdatedAt = 'UPDATED_AT',
}

/** Current status of the subscription */
export enum SubscriptionStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
  GracePeriod = 'GRACE_PERIOD',
  Pending = 'PENDING',
  Suspended = 'SUSPENDED',
  Trial = 'TRIAL',
}

/** Filter for subscription status */
export enum SubscriptionStatusFilter {
  Active = 'ACTIVE',
  All = 'ALL',
  Inactive = 'INACTIVE',
}

/** Reason a subscription was suggested as a candidate link for a sync result */
export enum SubscriptionSuggestionReason {
  Backfill = 'BACKFILL',
  Contiguous = 'CONTIGUOUS',
  Historical = 'HISTORICAL',
  NearRenewal = 'NEAR_RENEWAL',
  Overlap = 'OVERLAP',
}

/** Type of subscription based on customer lifecycle */
export enum SubscriptionType {
  Acquisition = 'ACQUISITION',
  Expansion = 'EXPANSION',
  Renewal = 'RENEWAL',
}

export enum SubscriptionVersionChangeType {
  AddComponent = 'ADD_COMPONENT',
  AddSubscriptionPrice = 'ADD_SUBSCRIPTION_PRICE',
  CancelPendingMigration = 'CANCEL_PENDING_MIGRATION',
  ChangeComponentQuantity = 'CHANGE_COMPONENT_QUANTITY',
  ChangePlan = 'CHANGE_PLAN',
  ExcludeComponent = 'EXCLUDE_COMPONENT',
  Grandfather = 'GRANDFATHER',
  IncludeComponent = 'INCLUDE_COMPONENT',
  InitialPin = 'INITIAL_PIN',
  MigrateBundle = 'MIGRATE_BUNDLE',
  MigrateComponent = 'MIGRATE_COMPONENT',
  MigrateModule = 'MIGRATE_MODULE',
  RemoveComponent = 'REMOVE_COMPONENT',
  RemoveSubscriptionPrice = 'REMOVE_SUBSCRIPTION_PRICE',
  Status = 'STATUS',
}

export enum SupportAiState {
  Answering = 'ANSWERING',
  HandedOver = 'HANDED_OVER',
  Off = 'OFF',
}

export enum SupportAuthorType {
  Agent = 'AGENT',
  Ai = 'AI',
  EndUser = 'END_USER',
  System = 'SYSTEM',
}

export enum SupportClientBillingStanding {
  Good = 'GOOD',
  NoActiveSubscription = 'NO_ACTIVE_SUBSCRIPTION',
  Overdue = 'OVERDUE',
}

/** Where an article Fin cited stands in this workspace's knowledge base, for the customer asking. MISSING: no article matches it. NOT_INDEXED: in the knowledge base, not searchable. HIDDEN: searchable, not by this customer's roles or plan. READABLE: this customer could read it. */
export enum SupportCompareFinSourceStatus {
  Hidden = 'HIDDEN',
  Missing = 'MISSING',
  NotIndexed = 'NOT_INDEXED',
  Readable = 'READABLE',
}

/** A view of a run's conversations that sets Revvly beside Fin. FIN_RESOLVED_REVVLY_NOT: Intercom recorded Fin as resolving it (assumed or confirmed) and Revvly's draft did not. FIN_ARTICLE_REVVLY_MISSED: Fin cited at least one article and Revvly's search returned none of them. */
export enum SupportCompareFocus {
  FinArticleRevvlyMissed = 'FIN_ARTICLE_REVVLY_MISSED',
  FinResolvedRevvlyNot = 'FIN_RESOLVED_REVVLY_NOT',
}

/** The most likely reason a conversation was not resolved, read off the articles Fin cited and what Revvly's search returned. FOUND_NOT_USED: the search returned Fin's article and Revvly still did not resolve. NOT_FOUND: the customer could read Fin's article and the search did not bring it up. HIDDEN_BY_ROLE: Fin's article is searchable, but not by this customer's roles or plan. NOT_INDEXED: it is in the knowledge base and left out of search. MISSING_ARTICLE: no article matches it. NO_FIN_SOURCE: Fin cited nothing. NOT_COMPARED: outside the rates for another reason. */
export enum SupportCompareGap {
  FoundNotUsed = 'FOUND_NOT_USED',
  HiddenByRole = 'HIDDEN_BY_ROLE',
  MissingArticle = 'MISSING_ARTICLE',
  NotCompared = 'NOT_COMPARED',
  NotFound = 'NOT_FOUND',
  NotIndexed = 'NOT_INDEXED',
  NoFinSource = 'NO_FIN_SOURCE',
  Resolved = 'RESOLVED',
}

/** Why Revvly gave no answer. NOTHING_RETRIEVED: the search found nothing this customer may read. NO_ANSWER: it found passages and the model said they did not answer. UNCITED: the draft cited nothing, so it would not have been shown. NO_ROLE: nothing found, and the customer matched none of the roles. ERROR: the search or the model failed. */
export enum SupportCompareReasonCode {
  Error = 'ERROR',
  NothingRetrieved = 'NOTHING_RETRIEVED',
  NoAnswer = 'NO_ANSWER',
  NoRole = 'NO_ROLE',
  Uncited = 'UNCITED',
}

export enum SupportCompareReferenceKind {
  Fin = 'FIN',
  Human = 'HUMAN',
  None = 'NONE',
}

export enum SupportCompareRevvlyOutcome {
  Answered = 'ANSWERED',
  Error = 'ERROR',
  Handover = 'HANDOVER',
}

/** Where a "Compare with Intercom" backtest run is. STOPPED_SPEND_CAP: the daily AI spend cap was reached; the results so far stand and the run can be resumed. */
export enum SupportCompareStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Queued = 'QUEUED',
  Running = 'RUNNING',
  StoppedSpendCap = 'STOPPED_SPEND_CAP',
}

/** The grader's verdict on Revvly's draft. UNGRADED (unreadable grade), ERROR (the draft failed), NO_REFERENCE (nothing to compare with), NO_ROLE (the customer's Intercom profile matched none of the workspace's roles, so only articles open to everyone were readable and none matched) and NO_QUESTION (the customer never asked anything: they tapped a quick reply and left) are counted and left out of the rates. */
export enum SupportCompareVerdict {
  Error = 'ERROR',
  Handover = 'HANDOVER',
  Incorrect = 'INCORRECT',
  NoQuestion = 'NO_QUESTION',
  NoReference = 'NO_REFERENCE',
  NoRole = 'NO_ROLE',
  Partial = 'PARTIAL',
  Ungraded = 'UNGRADED',
  WouldResolve = 'WOULD_RESOLVE',
}

/** What a guideline tells the AI: how to write, context to use, or what to leave to a person. */
export enum SupportGuidelineKind {
  CommunicationStyle = 'COMMUNICATION_STYLE',
  Context = 'CONTEXT',
  Handover = 'HANDOVER',
}

export enum SupportImportProvider {
  Intercom = 'INTERCOM',
}

export enum SupportImportStatus {
  Cancelled = 'CANCELLED',
  Cancelling = 'CANCELLING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Running = 'RUNNING',
}

export enum SupportMessageKind {
  InternalNote = 'INTERNAL_NOTE',
  Public = 'PUBLIC',
  System = 'SYSTEM',
}

/** NONE: one row for the range. TYPE: per ticket type. AGENT: per current assignee. DAY: per UTC day. */
export enum SupportMetricGroup {
  Agent = 'AGENT',
  Day = 'DAY',
  None = 'NONE',
  Type = 'TYPE',
}

/** What an agent can be notified about. */
export enum SupportNotificationKind {
  AssignedToYou = 'ASSIGNED_TO_YOU',
  Mentioned = 'MENTIONED',
  ReplyOnYourTicket = 'REPLY_ON_YOUR_TICKET',
  StatusChangedOnYourTicket = 'STATUS_CHANGED_ON_YOUR_TICKET',
  TicketCreated = 'TICKET_CREATED',
}

export enum SupportReplyKind {
  InternalNote = 'INTERNAL_NOTE',
  Public = 'PUBLIC',
}

/** ROLE compares the user's role value; ATTRIBUTE compares one of their attributes. */
export enum SupportRoleMatchSource {
  Attribute = 'ATTRIBUTE',
  Role = 'ROLE',
}

export enum SupportSearchMatchedIn {
  Message = 'MESSAGE',
  Number = 'NUMBER',
  Title = 'TITLE',
}

export enum SupportTicketView {
  All = 'ALL',
  Closed = 'CLOSED',
  Mine = 'MINE',
  Open = 'OPEN',
  Unassigned = 'UNASSIGNED',
  Untitled = 'UNTITLED',
  Waiting = 'WAITING',
}

/** Status of a sync result after extraction */
export enum SyncResultStatus {
  DiscardedNotInvoice = 'DISCARDED_NOT_INVOICE',
  Error = 'ERROR',
  Extracted = 'EXTRACTED',
  NeedsReview = 'NEEDS_REVIEW',
  Updated = 'UPDATED',
}

/** System-generated task kind (metered usage prompts and faults). */
export enum TaskKind {
  ActivationOverdue = 'ACTIVATION_OVERDUE',
  ManualUsage = 'MANUAL_USAGE',
  SeatRequest = 'SEAT_REQUEST',
  UnverifiedUsage = 'UNVERIFIED_USAGE',
}

/** How a task was created. */
export enum TaskOrigin {
  Agent = 'AGENT',
  Api = 'API',
  Direct = 'DIRECT',
}

/** Task priority (LOW=1 .. URGENT=4). Stored as an integer for urgency sorting. */
export enum TaskPriority {
  High = 'HIGH',
  Low = 'LOW',
  Normal = 'NORMAL',
  Urgent = 'URGENT',
}

/** Task list sort field. */
export enum TaskSortField {
  ClosedAt = 'CLOSED_AT',
  CreatedAt = 'CREATED_AT',
  DueDate = 'DUE_DATE',
  Name = 'NAME',
  Priority = 'PRIORITY',
  Status = 'STATUS',
  UpdatedAt = 'UPDATED_AT',
}

/** Task lifecycle state. */
export enum TaskStatus {
  Cancelled = 'CANCELLED',
  Doing = 'DOING',
  Done = 'DONE',
  Todo = 'TODO',
}

export enum TaxIdValidationStatus {
  Invalid = 'INVALID',
  Unvalidated = 'UNVALIDATED',
  Valid = 'VALID',
}

export enum TaxRateStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
}

export enum TaxReferenceBehaviour {
  Adds = 'ADDS',
  Country = 'COUNTRY',
  ReplacesCountry = 'REPLACES_COUNTRY',
}

export enum TaxReferenceDriftKind {
  RateChanged = 'RATE_CHANGED',
  Removed = 'REMOVED',
}

export enum TaxReferenceLayerStatus {
  Available = 'AVAILABLE',
  CodeTaken = 'CODE_TAKEN',
  Imported = 'IMPORTED',
  NeedsRegistration = 'NEEDS_REGISTRATION',
}

export enum TaxRegistrationStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
}

export enum TaxReportBasis {
  Accrual = 'ACCRUAL',
  Cash = 'CASH',
}

export enum TaxReportGroupBy {
  Jurisdiction = 'JURISDICTION',
  Month = 'MONTH',
  TaxCode = 'TAX_CODE',
}

export enum TaxTreatment {
  Exempt = 'EXEMPT',
  OutOfScope = 'OUT_OF_SCOPE',
  Reduced = 'REDUCED',
  ReverseCharge = 'REVERSE_CHARGE',
  Standard = 'STANDARD',
  ZeroRated = 'ZERO_RATED',
}

export enum TaxType {
  ConsumptionTax = 'CONSUMPTION_TAX',
  Gst = 'GST',
  Other = 'OTHER',
  SalesTax = 'SALES_TAX',
  Vat = 'VAT',
}

/** Headcount band */
export enum TeamSizeRange {
  Size_1_10 = 'SIZE_1_10',
  Size_11_50 = 'SIZE_11_50',
  Size_51_200 = 'SIZE_51_200',
  Size_201_1000 = 'SIZE_201_1000',
  Size_1000Plus = 'SIZE_1000_PLUS',
}

/** Where a self-serve workspace stands with its subscription. SUSPENDED and CANCELLED are read-only. */
export enum TenantBillingStatus {
  Active = 'ACTIVE',
  Blocked = 'BLOCKED',
  Cancelled = 'CANCELLED',
  PastDue = 'PAST_DUE',
  Suspended = 'SUSPENDED',
  Trialing = 'TRIALING',
}

/** Features that can be enabled/disabled per tenant */
export enum TenantFeature {
  Analytics = 'ANALYTICS',
  AssistantOnboarding = 'ASSISTANT_ONBOARDING',
  AutoRenewal = 'AUTO_RENEWAL',
  BillingSchedule = 'BILLING_SCHEDULE',
  Checkins = 'CHECKINS',
  Crm = 'CRM',
  DriveFolders = 'DRIVE_FOLDERS',
  EmailTier1Triage = 'EMAIL_TIER1_TRIAGE',
  EmailTier2Extraction = 'EMAIL_TIER2_EXTRACTION',
  FeatureFlags = 'FEATURE_FLAGS',
  Finance = 'FINANCE',
  Forms = 'FORMS',
  Import = 'IMPORT',
  Incubators = 'INCUBATORS',
  MarketConfig = 'MARKET_CONFIG',
  Okr = 'OKR',
  Organizations = 'ORGANIZATIONS',
  Pricing = 'PRICING',
  ProductConfig = 'PRODUCT_CONFIG',
  ProductUsage = 'PRODUCT_USAGE',
  Subscriptions = 'SUBSCRIPTIONS',
  Support = 'SUPPORT',
  Tax = 'TAX',
  TenantManagement = 'TENANT_MANAGEMENT',
  UserManagement = 'USER_MANAGEMENT',
  WorkInbox = 'WORK_INBOX',
}

/** Type of tenant and its role in the platform */
export enum TenantType {
  AcceleratorApp = 'ACCELERATOR_APP',
  Master = 'MASTER',
  Regular = 'REGULAR',
}

/** How an email thread was linked to an Organization. */
export enum ThreadResolutionMethod {
  ContactMatch = 'CONTACT_MATCH',
  DomainMatch = 'DOMAIN_MATCH',
  Manual = 'MANUAL',
  Unresolved = 'UNRESOLVED',
}

/** Thread-level triage rollup. */
export enum ThreadTriageVerdict {
  Pending = 'PENDING',
  Qualified = 'QUALIFIED',
  RejectedT0 = 'REJECTED_T0',
  RejectedT1 = 'REJECTED_T1',
}

export enum TicketChannel {
  Agent = 'AGENT',
  Api = 'API',
  IntercomImport = 'INTERCOM_IMPORT',
  Widget = 'WIDGET',
}

export enum TicketPriority {
  High = 'HIGH',
  Low = 'LOW',
  Normal = 'NORMAL',
  Urgent = 'URGENT',
}

/** How a ticket was closed. Only AI_RESOLVED is billable. */
export enum TicketResolution {
  AgentResolved = 'AGENT_RESOLVED',
  AiResolved = 'AI_RESOLVED',
  AutoClosed = 'AUTO_CLOSED',
  ClosedByRequester = 'CLOSED_BY_REQUESTER',
  Duplicate = 'DUPLICATE',
  WontFix = 'WONT_FIX',
}

/** What an unreported metered period means for this component: REVIEW (hold the invoice for a human) or ZERO (seal at zero automatically). */
export enum UnreportedUsagePolicy {
  Review = 'REVIEW',
  Zero = 'ZERO',
}

/** What happens to unused committed units at period end */
export enum UnusedUnitBehavior {
  Credit = 'CREDIT',
  Forfeit = 'FORFEIT',
  Rollover = 'ROLLOVER',
}

/** Type of action recorded in a usage audit entry */
export enum UsageAuditAction {
  CommissionEvent = 'COMMISSION_EVENT',
  Decrement = 'DECREMENT',
  Increment = 'INCREMENT',
  Override = 'OVERRIDE',
  PeriodReset = 'PERIOD_RESET',
  Reset = 'RESET',
  Rollback = 'ROLLBACK',
  Set = 'SET',
}

/** How a metered quantity reaches Revvly: API (integration increments it) or MANUAL (an operator enters it). */
export enum UsageCaptureMode {
  Api = 'API',
  Manual = 'MANUAL',
}

/** What an open usage reading asks of an operator: NEEDS_ENTRY (manual entry due), INTEGRATION_FAULT (unverified API period), AWAITING_SEAL (self-resolving), UNREPORTED (nothing ever reported — suspect capture-mode configuration), RESOLVED (settled). */
export enum UsageEntryDisposition {
  AwaitingSeal = 'AWAITING_SEAL',
  IntegrationFault = 'INTEGRATION_FAULT',
  NeedsEntry = 'NEEDS_ENTRY',
  Resolved = 'RESOLVED',
  Unreported = 'UNREPORTED',
}

/** Where a sealed reading’s quantity actually came from. */
export enum UsageReadingSource {
  Api = 'API',
  Manual = 'MANUAL',
  Override = 'OVERRIDE',
  ZeroDefault = 'ZERO_DEFAULT',
}

/** Lifecycle of a metered usage reading. Only SEALED and WAIVED allow the invoice to be issued. */
export enum UsageReadingStatus {
  Accumulating = 'ACCUMULATING',
  Awaiting = 'AWAITING',
  Sealed = 'SEALED',
  Unverified = 'UNVERIFIED',
  Waived = 'WAIVED',
}

/** Surface a usage mutation arrived through */
export enum UsageSource {
  Graphql = 'GRAPHQL',
  Internal = 'INTERNAL',
  Rest = 'REST',
  Webhook = 'WEBHOOK',
}

/** Actor that triggered a usage mutation */
export enum UsageTriggeredBy {
  AiAgent = 'AI_AGENT',
  Api = 'API',
  EndCustomer = 'END_CUSTOMER',
  System = 'SYSTEM',
  TenantUser = 'TENANT_USER',
  Webhook = 'WEBHOOK',
}

export enum UserRoles {
  Admin = 'Admin',
  AdminCoach = 'AdminCoach',
  Coach = 'Coach',
  Cofounder = 'Cofounder',
  CommunityMember = 'CommunityMember',
  Evaluator = 'Evaluator',
  Expert = 'Expert',
  Mentor = 'Mentor',
  Staff = 'Staff',
  SubAdmin = 'SubAdmin',
}

/** How a saved signature was made: drawn, typed or uploaded as an image. */
export enum UserSignatureKind {
  Drawn = 'DRAWN',
  Image = 'IMAGE',
  Typed = 'TYPED',
}

export enum UserStatuses {
  Active = 'Active',
  Archived = 'Archived',
  Deleted = 'Deleted',
  Deny = 'Deny',
  Pending = 'Pending',
  Unactive = 'Unactive',
}

/** Where a Variable Component Price applies. Resolution at billing time is most-specific-wins: SUBSCRIPTION > BUNDLE > MODULE > GLOBAL_DEFAULT. */
export enum VariableComponentPriceScope {
  Bundle = 'BUNDLE',
  GlobalDefault = 'GLOBAL_DEFAULT',
  Module = 'MODULE',
  Subscription = 'SUBSCRIPTION',
}

/** Lifecycle state of a Variable Component (DRAFT → PUBLISHED → ARCHIVED) */
export enum VariableComponentStatus {
  Archived = 'ARCHIVED',
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
}

/** Whether the variable is counted in whole units or decimal */
export enum VariableUnitType {
  Decimal = 'DECIMAL',
  Integer = 'INTEGER',
}

/** PENDING is owed now or waiting to retry; FAILED gave up after every retry; SKIPPED was dropped (endpoint off or removed, or too old). */
export enum WebhookDeliveryStatus {
  Delivered = 'DELIVERED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Skipped = 'SKIPPED',
}

/** A ticket event a webhook endpoint can subscribe to. The receiver sees the dotted value as `type`, e.g. ticket.status_changed. */
export enum WebhookEventType {
  TicketClosed = 'TICKET_CLOSED',
  TicketCreated = 'TICKET_CREATED',
  TicketMessage = 'TICKET_MESSAGE',
  TicketProgressed = 'TICKET_PROGRESSED',
  TicketReopened = 'TICKET_REOPENED',
  TicketStatusChanged = 'TICKET_STATUS_CHANGED',
  TicketUpdated = 'TICKET_UPDATED',
}

/** Whether Revvly created the task or an agent linked one that already existed. */
export enum WorkItemLinkMode {
  Created = 'CREATED',
  Linked = 'LINKED',
}

/** The tracker a work item lives in. */
export enum WorkItemProvider {
  Clickup = 'CLICKUP',
}
